import { cn } from "@/lib/utils";
import { AlertTriangle, RotateCcw, Home } from "lucide-react";
import { Component, ReactNode } from "react";

interface Props {
  children: ReactNode;
  /** If true, show a minimal inline fallback instead of full-page */
  inline?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Known "benign" errors that are caused by browser extensions, translation tools,
 * or other external DOM manipulation — NOT by our code.
 * These should be caught gracefully without alarming the user.
 */
const BENIGN_ERROR_PATTERNS = [
  "removeChild",
  "insertBefore",
  "appendChild",
  "The node to be removed is not a child",
  "not a child of this node",
  "n'est pas un enfant",
  "ist kein Kindknoten",
  "no es un hijo",
  "Failed to execute 'removeChild'",
  "Failed to execute 'insertBefore'",
  // Extension-related
  "ResizeObserver loop",
  "Script error",
];

function isBenignError(error: Error | null): boolean {
  if (!error) return false;
  const msg = `${error.message || ""} ${error.stack || ""}`;
  return BENIGN_ERROR_PATTERNS.some((pattern) => msg.includes(pattern));
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Only report non-benign errors to Sentry
    if (!isBenignError(error)) {
      try {
        import("@sentry/react").then((Sentry) => {
          Sentry.captureException(error, {
            extra: { componentStack: info.componentStack },
          });
        });
      } catch {
        // Sentry not available, ignore
      }
    }
    // Log to console for debugging
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      const benign = isBenignError(this.state.error);

      // For benign errors (browser translation, extensions), try to recover automatically
      if (benign) {
        return (
          <div className="flex items-center justify-center min-h-screen p-8">
            <div className="flex flex-col items-center w-full max-w-md p-8 text-center">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mb-6"
                style={{ background: "oklch(0.52 0.09 128 / 0.1)" }}
              >
                <AlertTriangle
                  size={32}
                  style={{ color: "oklch(0.52 0.09 128)" }}
                />
              </div>

              <h2
                className="text-xl font-bold mb-3"
                style={{ color: "oklch(0.2 0.02 80)" }}
              >
                Darstellungsproblem
              </h2>

              <p
                className="text-sm mb-6 leading-relaxed"
                style={{
                  color: "oklch(0.45 0.02 80)",
                  fontFamily: "var(--font-body)",
                }}
              >
                Es gab ein Problem mit der Darstellung. Dies kann durch
                Browser-Erweiterungen oder automatische Übersetzung verursacht
                werden. Bitte lade die Seite neu.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => window.location.reload()}
                  className={cn(
                    "flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:scale-105"
                  )}
                  style={{ background: "oklch(0.52 0.09 128)" }}
                >
                  <RotateCcw size={16} />
                  Seite neu laden
                </button>
              </div>
            </div>
          </div>
        );
      }

      // For real application errors, show a friendly message
      return (
        <div className="flex items-center justify-center min-h-screen p-8">
          <div className="flex flex-col items-center w-full max-w-md p-8 text-center">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mb-6"
              style={{ background: "oklch(0.6 0.15 25 / 0.1)" }}
            >
              <AlertTriangle size={32} style={{ color: "oklch(0.6 0.15 25)" }} />
            </div>

            <h2
              className="text-xl font-bold mb-3"
              style={{ color: "oklch(0.2 0.02 80)" }}
            >
              Etwas ist schiefgelaufen
            </h2>

            <p
              className="text-sm mb-6 leading-relaxed"
              style={{
                color: "oklch(0.45 0.02 80)",
                fontFamily: "var(--font-body)",
              }}
            >
              Ein unerwarteter Fehler ist aufgetreten. Bitte versuche es erneut
              oder kehre zur Startseite zurück.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => window.location.reload()}
                className={cn(
                  "flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:scale-105"
                )}
                style={{ background: "oklch(0.52 0.09 128)" }}
              >
                <RotateCcw size={16} />
                Neu laden
              </button>
              <a
                href="/"
                className={cn(
                  "flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all hover:scale-105 border"
                )}
                style={{
                  color: "oklch(0.4 0.02 80)",
                  borderColor: "oklch(0.85 0.02 80)",
                }}
              >
                <Home size={16} />
                Startseite
              </a>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
